import * as THREE from "three"
import { setScene, setModelsArr, setTruckModelsArr, setWheelModelsArr, setCamera, setCurrSection, setControls } from "./model"
import HDRbg from "../static/hdr_500.hdr"
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js"
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader"
import { boardData } from "./data/board-data"
import { truckData } from "./data/truck-data"
import { wheelData } from "./data/wheel-data"

const sceneGroup = new THREE.Group()

let tempModels = [],
  canvas,
  scene,
  renderer,
  camera,
  controls,
  pmremGenerator,
  sizes

const handleResize = () => {
  const w = document.querySelector(".canvas-container").getBoundingClientRect().width
  const h = document.querySelector(".canvas-container").getBoundingClientRect().height
  sizes.width = w
  sizes.height = h
  camera.aspect = w / h
  camera.updateProjectionMatrix()
  renderer.setSize(w, h)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
}

const init = () => {
  sizes = {
    width: document.querySelector(".canvas-container").getBoundingClientRect().width,
    height: document.querySelector(".canvas-container").getBoundingClientRect().height,
  }
  canvas = document.querySelector("canvas.webgl")
  scene = new THREE.Scene()
  setScene(scene)

  renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true })
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  renderer.shadowMap.enabled = true
  renderer.shadowMapSoft = true
  renderer.outputEncoding = THREE.sRGBEncoding
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.setSize(sizes.width, sizes.height)
  renderer.setClearColor(0x629c61, 0)

  camera = new THREE.PerspectiveCamera(30, sizes.width / sizes.height, 0.1, 500)
  setCamera(camera)
  scene.add(camera)

  controls = new OrbitControls(camera, canvas)
  controls.enableDamping = true
  controls.enabled = false
  controls.enableZoom = false

  setControls(controls)

  pmremGenerator = new THREE.PMREMGenerator(renderer)
  pmremGenerator.compileEquirectangularShader()
}

const loadLights = () => {
  new RGBELoader().setDataType(THREE.UnsignedByteType).load(HDRbg, function (texture) {
    const envMap = pmremGenerator.fromEquirectangular(texture).texture
    scene.environment = envMap
    texture.dispose()
    pmremGenerator.dispose()
    texture.encoding = THREE.RGBEEncoding
    setTimeout(() => {
      loadBoards()
    }, 1000)
  })

  const light2 = new THREE.DirectionalLight(0xffffff, 0.5)
  light2.position.set(20, 15, 9)
  light2.castShadow = true
  light2.shadow.bias = -0.0004
  scene.add(light2)
  // const helper2 = new THREE.DirectionalLightHelper(light2, 1, "red")
  // scene.add(helper2)

  const light3 = new THREE.DirectionalLight(0xffffff, 0.4)
  light3.position.set(1, 10, -2)
  light3.castShadow = true
  light3.shadow.bias = -0.0004
  scene.add(light3)
  // const helper3 = new THREE.DirectionalLightHelper(light3, 1, "blue")
  // scene.add(helper3)

  // let light6 = new THREE.AmbientLight(0xffffff, 0.1)
  // scene.add(light6)
}

/**
 * Tick
 */
const tick = () => {
  controls.update()
  renderer.render(scene, camera)
  window.requestAnimationFrame(tick)
}

// ==================== LOAD BOARDS ======================
// =======================================================
// =======================================================
const loadBoards = () => {
  const gltfLoader = new GLTFLoader()
  const numModels = boardData.length
  let loadCounter = 0

  for (let i = 0; i < numModels; i++) {
    const data = boardData[i]

    gltfLoader.load(data.src, (gltf) => {
      let model = gltf.scene
      let group = new THREE.Group()
      group.add(model)
      group.visible = false
      scene.add(group)

      // model.modelID = data.uid
      model.modelID = i

      // Add model object
      const newObj = {
        ...data,
        model,
        group,
      }
      tempModels[i] = newObj

      loadCounter++
      if (loadCounter === numModels) {
        sortArr()
        startExperience()
      }
      model.traverse(function (child) {
        if (child.isMesh) {
          child.receiveShadow = true
          child.castShadow = true
        }
      })
    })
  }
}

const sortArr = () => {
  const arr = tempModels.sort((firstItem, secondItem) => firstItem.id - secondItem.id)
  setModelsArr(arr)
}

// ==================== LOAD TRUCKS ======================
// =======================================================
// =======================================================
const loadTrucks = () => {
  const gltfLoader = new GLTFLoader(),
    numTrucks = truckData.length

  let tempArr = []

  for (let i = 0; i < numTrucks; i++) {
    const truck = truckData[i]
    gltfLoader.load(truckData[i].src, (gltf) => {
      let model = gltf.scene
      model.visible = false
      // Add model object
      const newObj = {
        ...truck,
        model,
      }
      tempArr[i] = newObj

      model.traverse(function (child) {
        if (child.isMesh) {
          child.receiveShadow = true
          child.castShadow = true
        }
      })
    })
  }
  setTruckModelsArr(tempArr)
}

// ==================== LOAD WHEELS ======================
// =======================================================
// =======================================================
const loadWheels = () => {
  const gltfLoader = new GLTFLoader(),
    numWheels = wheelData.length

  let tempArr = []

  for (let i = 0; i < numWheels; i++) {
    const wheel = wheelData[i]

    gltfLoader.load(wheelData[i].src, (gltf) => {
      let model = gltf.scene

      model.visible = false
      // Add model object
      const newObj = {
        ...wheel,
        model,
      }
      tempArr[i] = newObj

      model.traverse(function (child) {
        if (child.isMesh) {
          child.receiveShadow = true
          child.castShadow = true
        }
      })
    })
  }
  setWheelModelsArr(tempArr)
}

const startExperience = () => {
  /**
   *
   * Boards are loaded, start things off...
   *
   */
  setTimeout(() => {
    setCurrSection(0)
    loadTrucks()
    loadWheels()
  }, 0)

  // // Jump to sections for testing

  // // To Summary
  // setTimeout(() => {
  //   setCurrSection(3)
  // }, 5500)

  // // To Trucks
  // setTimeout(() => {
  //   setCurrSection(1)
  // }, 5000)

  // // To Wheels
  // setTimeout(() => {
  //   setCurrSection(2)
  // }, 9000)

  // // To Summary
  // setTimeout(() => {
  //   setCurrSection(3)
  // }, 12000)
}

/**
 * Initialize experience
 */
setTimeout(() => {
  init()
  loadLights()
  tick()
  window.addEventListener("resize", handleResize)
}, 1000)
